const SELECTOR = '[data-site-rating-widget]';
const STORAGE = {
    voted: 'siteRatingVoted',
    value: 'siteRatingValue',
};

const DEFAULT_TRANSLATIONS = {
    prompt: 'Оцените сайт',
    youRated: 'Вы поставили: :v/5',
    hover: 'Оценка: :v/5',
    sending: 'Отправляем…',
    thanks: 'Спасибо! Вы поставили: :v/5',
    already: 'Вы уже голосовали',
    error: 'Ошибка. Попробуйте позже',
    neterr: 'Ошибка сети',
    votes: 'голоса',
};

const isValidRating = (value) => Number.isInteger(value) && value >= 1 && value <= 5;

const parseRating = (value) => {
    const num = Number(value);
    if (!Number.isInteger(num)) {
        return null;
    }

    return isValidRating(num) ? num : null;
};

const getLocalStorageItem = (key) => {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};

const setLocalStorageItem = (key, value) => {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        /* ignored */
    }
};

const removeLocalStorageItem = (key) => {
    try {
        window.localStorage.removeItem(key);
    } catch {
        /* ignored */
    }
};

const getStoredRating = () => {
    const rating = parseRating(getLocalStorageItem(STORAGE.value));
    return rating === null ? null : String(rating);
};

const readStoredVote = () => {
    const rating = getStoredRating();
    if (!rating || getLocalStorageItem(STORAGE.voted) !== '1') {
        return null;
    }
    return rating;
};

const rememberRating = (value) => {
    const rating = parseRating(value);
    if (rating === null) {
        return null;
    }

    const text = String(rating);
    setLocalStorageItem(STORAGE.voted, '1');
    setLocalStorageItem(STORAGE.value, text);
    return text;
};

const clearStoredVote = () => {
    removeLocalStorageItem(STORAGE.voted);
    removeLocalStorageItem(STORAGE.value);
};

const hasStoredVote = () => getLocalStorageItem(STORAGE.voted) === '1';

const parseRateableId = (value) => {
    const candidate = Number(value);
    return Number.isInteger(candidate) && candidate > 0 ? candidate : null;
};

const resolveTargetPayload = (root) => {
    const dataset = root?.dataset ?? {};
    const payload = {};

    const rawType = dataset.ratingRateableType ?? dataset.rateableType;
    const rawId = dataset.ratingRateableId ?? dataset.rateableId;
    const rateableId = parseRateableId(rawId);

    if (rawType && rateableId !== null) {
        payload.rateable_type = rawType;
        payload.rateable_id = rateableId;
    }

    const targetKey = dataset.ratingTarget ?? dataset.target;
    if (targetKey) {
        payload.target = targetKey;
    }

    const explicitPage = dataset.ratingPage ?? dataset.page;
    if (explicitPage) {
        payload.page = explicitPage;
    } else if (typeof window !== 'undefined') {
        payload.page = window.location.href;
    }

    return payload;
};

const applyTargetToUrl = (url, target) => {
    if (!url || !target) {
        return;
    }

    if (target.page) {
        url.searchParams.set('page', target.page);
    }

    if (target.target) {
        url.searchParams.set('target', target.target);
    }

    if ('rateable_type' in target && 'rateable_id' in target) {
        url.searchParams.set('rateable_type', target.rateable_type);
        url.searchParams.set('rateable_id', String(target.rateable_id));
    }
};

const parseTranslations = (root) => {
    const raw = root.dataset.ratingTranslations;
    if (!raw) {
        return {...DEFAULT_TRANSLATIONS};
    }

    try {
        return {...DEFAULT_TRANSLATIONS, ...JSON.parse(raw)};
    } catch {
        return {...DEFAULT_TRANSLATIONS};
    }
};

const formatMessage = (template, value) => (template || '').replace(':v', value);

const ensureCsrfToken = () => {
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta) {
        return meta.getAttribute('content') || '';
    }

    const input = document.querySelector('input[name="_token"]');
    return input ? input.value : '';
};

const collectTargets = (scope) => {
    if (!scope) {
        return [];
    }

    if (scope === document) {
        return Array.from(document.querySelectorAll(SELECTOR));
    }

    const nodes = [];

    if (scope.matches?.(SELECTOR)) {
        nodes.push(scope);
    }

    if (scope.querySelectorAll) {
        nodes.push(...scope.querySelectorAll(SELECTOR));
    }

    return nodes;
};

const summarise = async (root, translations, updateDisplay, syncStarSelection, target) => {
    const summaryUrl = root.dataset.ratingSummaryUrl;
    if (!summaryUrl) {
        return;
    }

    try {
        const summary = new URL(summaryUrl, window.location.href);
        applyTargetToUrl(summary, target);

        const response = await fetch(summary.toString(), {
            credentials: 'same-origin',
            headers: {'X-Requested-With': 'XMLHttpRequest'},
        });

        if (!response.ok) {
            return;
        }

        const data = await response.json();

        const myRating = parseRating(data?.my_rating);
        if (myRating !== null && !hasStoredVote()) {
            rememberRating(myRating);
            syncStarSelection(myRating);
            updateDisplay(formatMessage(translations.youRated, myRating));
        }

        if (typeof data?.avg === 'number' && typeof data?.count === 'number' && data.count > 0) {
            const storedValue = getStoredRating();
            if (storedValue !== null) {
                updateDisplay(
                    `${formatMessage(translations.youRated, storedValue)} • ${data.avg.toFixed(1)} (${data.count} ${translations.votes})`,
                );
            } else {
                updateDisplay(translations.prompt);
            }
        }
    } catch (error) {
        console.error('Rating summary error:', error);
    }
};

const initWidget = (root) => {
    if (root.dataset.siteRatingInit === '1') {
        return;
    }

    root.dataset.siteRatingInit = '1';

    const translations = parseTranslations(root);
    const valueEl = root.querySelector('.rating-widget__value');
    const stars = Array.from(root.querySelectorAll('input[type="radio"]'));
    const labels = Array.from(root.querySelectorAll('label'));
    const target = resolveTargetPayload(root);
    const groupName = `site-rating-${Math.random().toString(36).slice(2, 8)}`;

    stars.forEach((input) => {
        input.name = groupName;
    });

    const updateDisplay = (text) => {
        if (valueEl) {
            valueEl.textContent = text || translations.prompt;
        }
    };

    updateDisplay();

    const syncStarSelection = (value) => {
        const matching = stars.find((input) => String(input.value) === String(value));
        if (matching) {
            matching.checked = true;
        }
    };

    const storedValue = getStoredRating();
    if (hasStoredVote() && storedValue !== null) {
        syncStarSelection(storedValue);
        updateDisplay(formatMessage(translations.youRated, storedValue));
    } else if (hasStoredVote() && storedValue === null) {
        clearStoredVote();
    }

    labels.forEach((label) => {
        label.addEventListener('mouseenter', () => {
            if (hasStoredVote()) {
                return;
            }

            const target = label.getAttribute('for');
            const input = stars.find((candidate) => candidate.id === target);
            if (input?.value) {
                updateDisplay(formatMessage(translations.hover, input.value));
            }
        });
    });

    root.addEventListener('mouseleave', () => {
        const current = getStoredRating();
        updateDisplay(current !== null ? formatMessage(translations.youRated, current) : translations.prompt);
    });

    stars.forEach((star) => {
        star.addEventListener('change', async () => {
            const selectedRating = parseInt(star.value, 10);
            if (Number.isNaN(selectedRating)) {
                return;
            }

            if (hasStoredVote()) {
                const previous = getStoredRating();
                if (previous !== null) {
                    syncStarSelection(previous);
                } else {
                    stars.forEach((input) => {
                        input.checked = false;
                    });
                }

                updateDisplay(translations.already);
                setTimeout(() => {
                    const current = getStoredRating();
                    updateDisplay(current !== null ? formatMessage(translations.youRated, current) : translations.prompt);
                }, 1200);
                return;
            }

            updateDisplay(translations.sending);

            try {
                const voteUrl = root.dataset.ratingVoteUrl;
                if (!voteUrl) {
                    updateDisplay(translations.error);
                    return;
                }

                const response = await fetch(voteUrl, {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'X-CSRF-TOKEN': ensureCsrfToken(),
                    },
                    body: JSON.stringify({
                        ...target,
                        rating: selectedRating,
                    }),
                });

                const payload = await response.json().catch(() => ({}));

                if (payload?.already_voted) {
                    const remembered = rememberRating(parseRating(payload?.my_rating) ?? selectedRating);
                    if (remembered !== null) {
                        syncStarSelection(remembered);
                    }
                    updateDisplay(translations.already);
                    return;
                }

                if (response.ok && (payload?.ok === true || payload?.ok === undefined)) {
                    const remembered = rememberRating(selectedRating);
                    if (remembered !== null) {
                        syncStarSelection(remembered);
                    }

                    if (typeof payload?.avg === 'number' && typeof payload?.count === 'number') {
                        updateDisplay(
                            `${formatMessage(translations.thanks, remembered ?? selectedRating)} ★ ${payload.avg.toFixed(1)} (${payload.count} ${translations.votes})`,
                        );
                    } else {
                        updateDisplay(formatMessage(translations.thanks, remembered ?? selectedRating));
                    }
                } else {
                    updateDisplay(translations.error);
                }
            } catch {
                updateDisplay(translations.neterr);
            }
        });
    });

    summarise(root, translations, updateDisplay, syncStarSelection, target);
};

const initialise = (scope) => {
    collectTargets(scope).forEach(initWidget);
};

let livewireHooked = false;

const setupLivewire = () => {
    if (livewireHooked || typeof window === 'undefined') {
        return;
    }

    const register = () => {
        if (window.Livewire?.hook) {
            window.Livewire.hook('message.processed', (_message, component) => {
                initialise(component?.el ?? document);
            });
            livewireHooked = true;
        }
    };

    if (window.Livewire) {
        register();
    }

    document.addEventListener('livewire:load', () => {
        register();
    }, {once: true});
};

export const initSiteRating = (scope = document) => {
    if (scope === document && document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            initialise(document);
        }, {once: true});
    } else {
        initialise(scope);
    }

    setupLivewire();
};

if (typeof window !== 'undefined') {
    window.initSiteRating = initSiteRating;
    initSiteRating();
}

export default initSiteRating;
